<script>
    import Spinner from "../components/Spinner.svelte";
	import { user, token } from '../stores.js';
	import { apiPost } from '../api.js';
	import { maxExpiry, expiryToMinutes, expiryShortcuts, formatLocalDateTime } from '../expiry.js';

	let { params } = $props();

	user.useLocalStorage();

	const url = "SECURESHARE_API_URL";
	const api_key = "SECURESHARE_API_KEY";

    let signedLinkPromise = $state(null);
	let note = $state("");
	let memo = $state("");

	const latestExpiry = maxExpiry();
	// Request links are emailed to a recipient who needs time to respond,
	// so default to the configured max (the pre-picker behavior) rather
	// than the send page's 1-hour default. The user can shorten it.
	let expiryDateTime = $state(latestExpiry);
	const shortcuts = expiryShortcuts();

    async function requestSignedLink() {
		const linkRequest = {
			creator_name: $user.name,
			creator_email: $user.email,
			note: note || undefined,
			memo: memo,
			expiry_in_minutes: expiryToMinutes(expiryDateTime)
		};

		const res = await apiPost(`${url}/generate_link`, linkRequest, { token: $token, apiKey: api_key });
		const json = await res.json();
		if (res.ok) {
			// The backend returns an absolute URL when SECURESHARE_BASE_URL
			// is set in the Lambda env, and a path-only URL (e.g.
			// "/respond/abc123") when it isn't. Prepend the current
			// origin in the path-only case so copied / displayed links
			// always include the scheme + host. See also
			// app.py:generate_link and secret_emailer's email links,
			// which have the same env-var dependency and need to be
			// fixed Lambda-side.
			const returned = json.url || '';
			return returned.startsWith('http')
				? returned
				: `${window.location.origin}${returned.startsWith('/') ? '' : '/'}${returned}`;
		} else {
			throw new Error(json);
		}
	}

	function generateLink() {
		signedLinkPromise = requestSignedLink();
	}

	let linkCopied = $state(false);
	async function copyLink(url) {
		await navigator.clipboard.writeText(url);
		linkCopied = true;
		setTimeout(() => linkCopied = false, 2000);
	}
</script>

<main class="container">
    <h1>Request</h1>
	<p>Create a one-time link that allows someone to securely send you a message. Share the link with them — no account required on their end. Once they submit, you'll be able to read the message from your home screen.</p>

	{#if signedLinkPromise}
		{#await signedLinkPromise}
			<Spinner></Spinner>
		{:then signed_link}
			<div class="alert alert-success">Request link created!</div>
			<p>Share this link with the recipient. They can use it to securely send you a message without needing a SecureShare account.</p>
			<p class="link-display">{signed_link}
				<button class="copy-btn" onclick={() => copyLink(signed_link)}>
					{#if linkCopied}
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>
					{:else}
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/><path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/></svg>
					{/if}
				</button>
			</p>
		{:catch error}
			<p class="alert alert-danger">Failed to create request link.</p>
		{/await}
	{:else}
		<div class="form-item">
			<label for="memo" class="form-label">Memo <span class="required">*</span></label>
			<span class="hint">A short message the recipient will see when they open the link — give them context for what they're being asked to send. <strong>Not encrypted</strong> — do not include sensitive information here.</span>
			<input name="memo" type="text" bind:value={memo} maxlength=500 class="form-control" autocomplete="off" required>
		</div>

		<div class="form-item">
			<label for="note" class="form-label">Internal Note</label>
			<span class="hint"><i>Optional</i> - For your reference only. The recipient will never see this.</span>
			<input name="note" type="text" bind:value={note} maxlength=500 class="form-control" autocomplete="off">
		</div>

		<div class="form-item">
			<label for="expiry" class="form-label">Expiry</label>
			<span class="hint">The request link will be permanently deleted after this time.</span>
			<input type="datetime-local" bind:value={expiryDateTime} max={latestExpiry} class="form-control">
			<div class="expiry-shortcuts">
				{#each shortcuts as s}
					<button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => expiryDateTime = formatLocalDateTime(Date.now() + s.ms)}>{s.label}</button>
				{/each}
			</div>
		</div>

		<div>
			<button onclick={generateLink} disabled={memo.trim() === ""} class="btn btn-primary">Create Request</button>
		</div>
	{/if}
</main>

<style>
	input[type=text] {
		width: 500px;
	}

	input[type=datetime-local] {
		max-width: 250px;
	}

	.expiry-shortcuts {
		margin-top: .5rem;
		display: flex;
		gap: .25rem;
	}

	.expiry-shortcuts :global(.btn) {
		font-size: 75%;
		padding: .15rem .35rem;
	}

	.link-display {
		font-size: 85%;
	}

	.copy-btn {
		background: none;
		border: none;
		padding: 0 .25rem;
		cursor: pointer;
		color: #6c757d;
		vertical-align: middle;
	}

	.copy-btn:hover {
		color: #0d6efd;
	}

	.required {
		color: #dc3545;
		margin-left: .25rem;
	}
</style>
